// hardcoded-ui-text: allow — these fill the validationError the context exposes; the screens
// that read it show their own copy and redirect, never this text.
import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { User as AppUser, Tenant } from '@/lib/database-types';
import { loggingService } from '@/shared/services/LoggingService';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

// Which kind of failure produced validationError, so callers (ProtectedRoute,
// Auth) can react correctly instead of treating every failure as "not
// authenticated": a network blip should retry in place, a permission problem
// is an account issue to show inline, and only a real expired/invalid
// session should send the user back to the login screen.
export type ValidationErrorKind = 'network' | 'permission' | 'expired' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: AppUser | null;
  tenantId: string | null;
  tenantData: Tenant | null;
  loading: boolean;
  databaseValidated: boolean;
  validationError: string | null;
  validationErrorKind: ValidationErrorKind;
  isRetryingConnection: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  tenantDiscoveryOptions: unknown;
  refreshTenantDiscovery: (email: string) => Promise<void>;
}

// A data or network failure is not "not authenticated" -- classify it so the
// screens that read validationError can tell the three cases apart (T-08).
function classifyAuthError(error: unknown): 'network' | 'permission' | 'expired' {
  const message = error instanceof Error ? error.message : String((error as { message?: unknown })?.message ?? '');
  const code = (error as { code?: string } | null)?.code;
  if (code === '42501') return 'permission';
  if (/failed to fetch|networkerror|network request failed|load failed|ECONNREFUSED|ETIMEDOUT/i.test(message)) {
    return 'network';
  }
  return 'expired';
}

const NETWORK_RETRY_DELAYS_MS = [2000, 4000, 8000, 15000];

// Exported for internal hooks that need to read the auth state without
// requiring an AuthProvider in test environments (e.g. UDL hooks at
// @/lib/data/hooks/). Production code should use `useAuth()` which throws if
// the provider is missing.
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<AppUser | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantData, setTenantData] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [databaseValidated, setDatabaseValidated] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationErrorKind, setValidationErrorKind] = useState<ValidationErrorKind>(null);
  const [isRetryingConnection, setIsRetryingConnection] = useState(false);
  const [tenantDiscoveryOptions, setTenantDiscoveryOptions] = useState<unknown>(null);
  const hasRedirectedRef = useRef(false);
  const isValidatingRef = useRef(false);
  const isFetchingProfileRef = useRef(false);
  const userProfileLoadedRef = useRef(false);
  const networkRetryAttemptRef = useRef(0);
  const networkRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper to check if we're in a platform admin area
  const isPlatformArea = () => {
    const path = window.location.pathname;
    return path.startsWith('/platform-admin') || path.startsWith('/secure-admin-portal');
  };

  // Enhanced database validation using the new validate_regular_user_session function
  const validateDatabaseSession = async (userId: string) => {
    // Skip validation if we're in platform admin area
    if (isPlatformArea()) {
      console.log('AuthContext: Skipping validation for platform admin area');
      return true;
    }

    // Skip if already validated and we have tenant ID
    if (databaseValidated && tenantId) {
      console.log('AuthContext: Database already validated with tenant ID, skipping');
      return true;
    }

    // Prevent concurrent validations
    if (isValidatingRef.current) {
      console.log('AuthContext: Validation already in progress, skipping');
      return false;
    }

    isValidatingRef.current = true;

    try {
      console.log('AuthContext: Starting database session validation for user:', userId);
      setValidationError(null);
      setValidationErrorKind(null);

      // Use the new regular user validation function
      const { data: validationResult, error } = await supabase.rpc('validate_regular_user_session');

      console.log('AuthContext: Validation RPC result:', { validationResult, error });

      if (error) {
        console.error('AuthContext: Database session validation RPC failed:', error);
        const kind = classifyAuthError(error);
        // A network or data-layer hiccup is not "not authenticated" -- retry
        // in place with growing delays instead of handing ProtectedRoute a
        // reason to redirect to /auth (T-08).
        if (kind === 'network' && networkRetryAttemptRef.current < NETWORK_RETRY_DELAYS_MS.length) {
          const delay = NETWORK_RETRY_DELAYS_MS[networkRetryAttemptRef.current];
          networkRetryAttemptRef.current += 1;
          setIsRetryingConnection(true);
          setValidationErrorKind('network');
          if (networkRetryTimerRef.current) clearTimeout(networkRetryTimerRef.current);
          networkRetryTimerRef.current = setTimeout(() => validateDatabaseSession(userId), delay);
          return false;
        }
        setValidationError(error.message);
        setValidationErrorKind(kind);
        setDatabaseValidated(false);
        setIsRetryingConnection(false);
        return false;
      }

      // The RPC answered: any earlier network retry sequence is over.
      networkRetryAttemptRef.current = 0;
      setIsRetryingConnection(false);

      if (!validationResult) {
        console.warn('AuthContext: Database session validation returned null result');
        setValidationError('Validation function returned no data');
        setValidationErrorKind('expired');
        setDatabaseValidated(false);
        return false;
      }
      
      // Process validation result
      const result = validationResult as Record<string, unknown>;
      if (typeof result !== 'object' || result === null) {
        console.error('AuthContext: Invalid validation result format:', result);
        setValidationError('Invalid validation result format');
        setDatabaseValidated(false);
        return false;
      }
      
      // Handle authenticated but no tenant (needs onboarding)
      if (result.needs_onboarding) {
        console.log('AuthContext: User authenticated but needs tenant setup (onboarding)');
        setDatabaseValidated(true);
        setTenantId(null);
        return true;
      }

      // Check if validation was successful
      if (!result.valid) {
        console.warn('AuthContext: Database session validation failed:', result.error);
        setValidationError(result.error || 'Session validation failed');
        setValidationErrorKind('expired');

        // If platform admin trying to access tenant area, redirect them
        if (result.error === 'Platform admins cannot access tenant areas' && !hasRedirectedRef.current) {
          hasRedirectedRef.current = true;
          window.location.replace('/secure-admin-portal');
        }

        setDatabaseValidated(false);
        return false;
      }
      
      // Set tenant ID from validation result
      if (result.tenant_id) {
        console.log('AuthContext: Setting tenantId from validation:', result.tenant_id);
        setTenantId(result.tenant_id);
      } else {
        console.log('AuthContext: No tenant_id in validation result, trying direct lookup');
        
        // Fallback: try to get tenant ID directly
        const { data: directTenantId } = await supabase.rpc('get_current_user_tenant_id');
        if (directTenantId) {
          console.log('AuthContext: Found tenant ID via direct lookup:', directTenantId);
          setTenantId(directTenantId);
        }
      }
      
      console.log('AuthContext: Database validation successful');
      setDatabaseValidated(true);
      return true;
      
    } catch (error) {
      console.error('AuthContext: Database session validation error:', error);
      const kind = classifyAuthError(error);
      if (kind === 'network' && networkRetryAttemptRef.current < NETWORK_RETRY_DELAYS_MS.length) {
        const delay = NETWORK_RETRY_DELAYS_MS[networkRetryAttemptRef.current];
        networkRetryAttemptRef.current += 1;
        setIsRetryingConnection(true);
        setValidationErrorKind('network');
        if (networkRetryTimerRef.current) clearTimeout(networkRetryTimerRef.current);
        networkRetryTimerRef.current = setTimeout(() => validateDatabaseSession(userId), delay);
        return false;
      }
      setValidationError(error instanceof Error ? error.message : 'Database validation failed');
      setValidationErrorKind(kind);
      setDatabaseValidated(false);
      setIsRetryingConnection(false);
      return false;
    } finally {
      isValidatingRef.current = false;
    }
  };

  const refreshTenantDiscovery = async (email: string) => {
    try {
      const { data, error } = await supabase.rpc('get_tenant_discovery_options', {
        user_email: email
      });
      
      if (error) {
        // Error fetching tenant discovery options
        return;
      }

      setTenantDiscoveryOptions(data);
    } catch (error) {
      // Unexpected error during tenant discovery
    }
  };

  // Fetch user profile and tenant ID for regular users only
  const fetchUserProfile = async (userId: string) => {
    // Skip entirely if we're in platform admin area
    if (isPlatformArea()) {
      return;
    }

    // Prevent concurrent profile fetches
    if (isFetchingProfileRef.current) {
      console.log('AuthContext: Profile fetch already in progress, skipping');
      return;
    }

    isFetchingProfileRef.current = true;
    
    try {
      // The profile read does not depend on the validation's answer, so both go out together and the
      // profile is used only when the session validates (the answer of an invalid session is dropped).
      // A thrown failure is held and re-thrown below, so the retry path sees it exactly as before.
      const profileRequest = Promise.resolve(supabase.rpc('get_current_tenant_user')).then(
        (result) => ({ result, thrown: null as unknown }),
        (thrown: unknown) => ({ result: null, thrown }),
      );

      // CRITICAL: Validate database session first
      const isValid = await validateDatabaseSession(userId);
      if (!isValid) {
        // Database validation failed, skipping profile fetch
        return;
      }

      // Fetching user profile for regular user

      // Get tenant user from tenant schema using the new function
      const settled = await profileRequest;
      if (settled.thrown) throw settled.thrown;
      const { data, error } = settled.result!;

      if (error) {
        // Error fetching tenant user profile
        return;
      }

      if (!data) {
        // No tenant user profile found
        setUserProfile(null);
        return;
      }

      // Cast the Json data to User type
      setUserProfile(data as Record<string, unknown>);
      userProfileLoadedRef.current = true;  // Mark profile as loaded
      // User profile loaded successfully
      
      // Initialize logging service with user and tenant context
      loggingService.setUserContext(userId);
      if (tenantId) {
        loggingService.setTenantContext(tenantId);
      }
      
    } catch (error) {
      // Error fetching user profile. A network failure here is the same case
      // validateDatabaseSession retries -- do not turn it into an expulsion.
      const kind = classifyAuthError(error);
      if (kind === 'network' && networkRetryAttemptRef.current < NETWORK_RETRY_DELAYS_MS.length) {
        const delay = NETWORK_RETRY_DELAYS_MS[networkRetryAttemptRef.current];
        networkRetryAttemptRef.current += 1;
        setIsRetryingConnection(true);
        setValidationErrorKind('network');
        if (networkRetryTimerRef.current) clearTimeout(networkRetryTimerRef.current);
        networkRetryTimerRef.current = setTimeout(() => fetchUserProfile(userId), delay);
        return;
      }
      setDatabaseValidated(false);
      setValidationError(error instanceof Error ? error.message : 'Profile fetch failed');
      setValidationErrorKind(kind);
      setIsRetryingConnection(false);
    } finally {
      isFetchingProfileRef.current = false;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id);
    }
  };

  // Track previous session to detect actual changes (OUTSIDE useEffect to persist)
  const previousSessionRef = useRef<Session | null>(null);

  useEffect(() => {
    // AuthContext: Initializing authentication
    console.log('AuthContext: Initializing authentication');

    // Set up auth state listener FIRST to prevent missing events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('AuthContext: Auth state change event:', event, !!session);

        // Capture previous user ID before ref updates (for duplicate SIGNED_IN detection)
        const previousUserId = previousSessionRef.current?.user?.id;

        // Check if session data actually changed
        const sessionChanged = !previousSessionRef.current ||
                               previousSessionRef.current.access_token !== session?.access_token ||
                               previousSessionRef.current.user?.id !== session?.user?.id;

        // Update tracking reference
        previousSessionRef.current = session;

        // Only update state if session data actually changed (prevents unnecessary re-renders)
        if (sessionChanged) {
          setSession(session);
          setUser(session?.user ?? null);
        }

        // Handle password recovery flow specially
        if (event === 'PASSWORD_RECOVERY') {
          console.log('AuthContext: Password recovery event detected - letting ResetPassword component handle UI');
          setLoading(false);
          // Don't fetch user profile or do other initialization
          // The ResetPassword component will handle the password reset flow
          // Session contains the recovery token needed for updateUser()
          return;
        }

        if (session?.user) {
          // Handle different event types differently
          if (event === 'TOKEN_REFRESHED') {
            // Silent refresh - don't show loading or refetch profile
            console.log('AuthContext: Token refreshed silently');
            return;
          }

          // Skip processing if this is a duplicate SIGNED_IN (tab focus recovery)
          // Check if we already have this exact session loaded
          console.log('🔍 [DEBUG] SIGNED_IN check:', {
            event,
            previousUserId,
            sessionUserId: session.user.id,
            userProfileState: userProfile !== null ? 'SET' : 'NULL',
            userProfileLoadedRef: userProfileLoadedRef.current,
            previousSessionExists: !!previousSessionRef.current,
            idsMatch: previousUserId === session.user.id,
            profileLoaded: userProfileLoadedRef.current,
            willSkip: event === 'SIGNED_IN' && previousUserId === session.user.id && userProfileLoadedRef.current
          });

          if (event === 'SIGNED_IN' &&
              previousUserId === session.user.id &&
              userProfileLoadedRef.current) {
            console.log('✅ AuthContext: SIGNED_IN is session recovery (tab focus), skipping duplicate processing');
            return;  // SKIP: User already authenticated with this exact session
          }

          console.log('❌ AuthContext: User authenticated, fetching profile (check failed)');
          setLoading(true);

          // Defer all async operations to prevent blocking auth state changes
          setTimeout(async () => {
            try {
              // AuthContext is ONLY for regular users - fetch profile directly
              await fetchUserProfile(session.user.id);

              // Invalidate permissions cache to ensure fresh permissions after login
              // (handles role/profile changes that occurred while user was logged out)
              queryClient.invalidateQueries({
                queryKey: ['user-permissions', session.user.id]
              });
            } catch (error) {
              console.error('AuthContext: Error fetching profile, retrying:', error);
              await fetchUserProfile(session.user.id);
            } finally {
              setLoading(false);
            }
          }, 0);
        } else {
          console.log('AuthContext: User signed out, clearing data');

          // Invalidate permissions cache on logout
          if (user?.id) {
            queryClient.invalidateQueries({
              queryKey: ['user-permissions', user.id]
            });
          }

          setUserProfile(null);
          userProfileLoadedRef.current = false;  // Reset profile loaded flag on sign-out
          setTenantId(null);
          setTenantData(null);
          setDatabaseValidated(false);
          setValidationError(null);
          setValidationErrorKind(null);
          setIsRetryingConnection(false);
          networkRetryAttemptRef.current = 0;
          if (networkRetryTimerRef.current) {
            clearTimeout(networkRetryTimerRef.current);
            networkRetryTimerRef.current = null;
          }

          // Clear logging context when user signs out
          loggingService.clearUserContext();

          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    const initializeAuth = async () => {
      try {
        console.log('AuthContext: Checking for existing session');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        console.log('AuthContext: Session check result:', { hasSession: !!session, error });
        
        if (error) {
          console.error('AuthContext: Error getting session:', error);
          setLoading(false);
          return;
        }
        
        // If no session on init, auth state change handler will handle it
        if (!session) {
          console.log('AuthContext: No existing session found');
          setLoading(false);
          return;
        }
        
        console.log('AuthContext: Found existing session for user:', session.user.email);
        // Session exists, the auth state change handler will process it
        // but we still need to set session state here for immediate access
        setSession(session);
        setUser(session.user);
      } catch (error) {
        console.error('AuthContext: Error initializing auth:', error);
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      console.log('AuthContext: Cleaning up auth listener');
      subscription.unsubscribe();
      if (networkRetryTimerRef.current) {
        clearTimeout(networkRetryTimerRef.current);
      }
    };
  }, []);

  // Fetch and cache tenant data when tenantId changes
  useEffect(() => {
    if (!tenantId) {
      setTenantData(null);
      return;
    }

    const fetchTenantData = async () => {
      try {
        console.log('AuthContext: Fetching tenant data for ID:', tenantId);

        const { data, error } = await supabase
          .from('tenants')
          .select('*')
          .eq('id', tenantId)
          .single();

        if (error) {
          console.error('AuthContext: Error fetching tenant data:', error);
          return;
        }

        console.log('AuthContext: Tenant data cached:', data?.name);
        setTenantData(data);
      } catch (error) {
        console.error('AuthContext: Error fetching tenant data:', error);
      }
    };

    fetchTenantData();
  }, [tenantId]);

  // Set Sentry user context for error tracking
  useEffect(() => {
    if (user && tenantId) {
      Sentry.setUser({
        id: user.id,
        email: user.email || undefined,
        username: user.user_metadata?.full_name || undefined,
        tenant_id: tenantId,
        schema_name: user.user_metadata?.schema_name || undefined,
      });
    } else if (!user) {
      Sentry.setUser(null);
    }
  }, [user, tenantId]);

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      // Error signing out
    }
  };

  const value = {
    user,
    session,
    userProfile,
    tenantId,
    tenantData,
    loading,
    databaseValidated,
    validationError,
    validationErrorKind,
    isRetryingConnection,
    signOut,
    refreshProfile,
    tenantDiscoveryOptions,
    refreshTenantDiscovery
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}